import {
  BriefcaseBusiness,
  Circle,
  Compass,
  Route,
  ShieldCheck,
} from "lucide-react";
import { CommandPalette } from "./components/CommandPalette";
import { SkillGraph } from "./components/SkillGraph";
import { WaitlistForm } from "./components/WaitlistForm";
import { databaseSummary } from "./data/skillDatabase";

const transitionExamples = [
  "Frontend Engineer → Product Manager",
  "Backend Engineer → Engineering Manager",
  "QA Engineer → Automation Engineer",
  "UX Designer → Product Designer",
  "Data Analyst → Data Engineer",
  "Software Engineer → AI Engineer",
];

const faqItems = [
  {
    question: "Can a software engineer change careers without starting over?",
    answer:
      "Yes. Many adjacent roles value the same technical judgment, communication, systems thinking, and product context. Career Path is designed to show which strengths transfer and which specific gaps are worth closing.",
  },
  {
    question: "How does the developer-to-product-manager transition work?",
    answer:
      "The transition usually builds on technical knowledge, product collaboration, and user understanding. The common gaps are roadmapping, discovery, prioritization, and business metrics—not your entire professional foundation.",
  },
  {
    question: "Which alternative careers suit programmers?",
    answer:
      "Realistic options depend on the work you enjoy. Common adjacent paths include technical product management, solutions engineering, developer advocacy, engineering management, data engineering, and AI engineering.",
  },
  {
    question: "Is this a generic AI career coach?",
    answer:
      "No. The product is centered on structured role-and-skill mappings, transparent match logic, and concrete transition gaps. The interface is a career map, not a chatbot.",
  },
];

function App() {
  return (
    <div className="site-shell">
      <header className="site-header">
        <div className="nav-bar">
          <a className="wordmark" href="#top" aria-label="Career Path home">
            <span className="wordmark__mark" aria-hidden="true"><Route size={20} /></span>
            Career Path
          </a>
          <nav className="nav-links" aria-label="Primary navigation">
            <a href="#skill-map">Skill map</a>
            <a href="#how-it-works">How it works</a>
            <a href="#faq">FAQ</a>
          </nav>
          <div className="nav-actions">
            <CommandPalette />
            <a className="button button--nav" href="#early-access">Get early access</a>
          </div>
        </div>
      </header>

      <main id="main">
        <section className="hero" id="top">
          <div className="hero__copy hero-reveal" style={{ "--i": 0 } as React.CSSProperties}>
            <p className="hero__positioning">Find your next career path without starting from zero.</p>
            <h1>Discover your next career path in tech</h1>
            <p className="hero__lede">
              See which roles match your existing skills, what you are missing, and the fastest path to transition.
            </p>
            <WaitlistForm source="hero" />
            <dl className="dataset-proof" aria-label="Initial dataset coverage">
              <div><dt>{databaseSummary.roles}</dt><dd>IT roles mapped</dd></div>
              <div><dt>{databaseSummary.skills}+</dt><dd>skills connected</dd></div>
              <div><dt>{databaseSummary.relationships}</dt><dd>starter transitions</dd></div>
            </dl>
          </div>
          <figure className="hero__visual hero-reveal" style={{ "--i": 1 } as React.CSSProperties}>
            <picture>
              <source srcSet="/career-path-ios.webp" type="image/webp" />
              <img
                src="/career-path-ios.png"
                alt="Career Path iOS home screen showing a Frontend Engineer to Technical Product Manager transition, 72 percent readiness, skills, and next actions"
                width="864"
                height="1821"
                fetchPriority="high"
              />
            </picture>
            <figcaption>A first look at the Career Path mobile experience.</figcaption>
          </figure>
        </section>

        <section className="transition-strip" aria-label="Example career transitions">
          <div className="transition-strip__track">
            {transitionExamples.map((example) => <span key={example}>{example}</span>)}
          </div>
        </section>

        <section className="skill-section" id="skill-map">
          <header className="section-heading section-heading--dark">
            <p>Career advice gets useful when it names the gap.</p>
            <h2>See the path, not just the destination.</h2>
          </header>
          <SkillGraph />
        </section>

        <section className="method-section" id="how-it-works">
          <header className="section-heading">
            <h2>Your experience is the starting point.</h2>
            <p>Career Path compares the work you can already do with the work each adjacent role actually asks for.</p>
          </header>
          <ol className="method-list">
            <li>
              <span>1</span>
              <div><Compass aria-hidden="true" size={24} /><h3>Map what you know</h3><p>Capture technical, product, design, business, and leadership skills—not just job titles.</p></div>
            </li>
            <li>
              <span>2</span>
              <div><BriefcaseBusiness aria-hidden="true" size={24} /><h3>Compare adjacent roles</h3><p>See credible transitions ranked by transferable strengths and role-specific requirements.</p></div>
            </li>
            <li>
              <span>3</span>
              <div><Route aria-hidden="true" size={24} /><h3>Build the missing proof</h3><p>Focus on the courses, projects, and positioning work that make the next move believable.</p></div>
            </li>
          </ol>
        </section>

        <section className="benchmark-section" id="benchmarks">
          <header className="section-heading section-heading--benchmark">
            <div>
              <span className="benchmark-label">FUTURE BENCHMARK</span>
              <h2>What changes after the move?</h2>
            </div>
            <p>
              Reliable, transition-specific benchmarks are still limited. Instead of inventing claims, Career Path will collect anonymized outcomes from people who make a move.
            </p>
          </header>
          <div className="benchmark-grid" aria-label="Planned anonymized transition benchmarks">
            {[
              ["Salary", "Before-and-after compensation"],
              ["Job satisfaction", "Fit with the day-to-day work"],
              ["Work-life balance", "Hours, flexibility, and energy"],
              ["Career confidence", "Clarity about the next step"],
            ].map(([title, description]) => (
              <article key={title}>
                <span aria-hidden="true">—</span>
                <h3>{title}</h3>
                <p>{description}</p>
                <small>Outcome data pending</small>
              </article>
            ))}
          </div>
          <p className="benchmark-note"><ShieldCheck aria-hidden="true" size={18} /> Planned reporting will be aggregated and anonymized.</p>
        </section>

        <section className="role-ledger" aria-labelledby="role-ledger-title">
          <header className="section-heading">
            <h2 id="role-ledger-title">Built around real tech career paths.</h2>
            <p>Engineering, product, design, data, and customer-facing technical work live in one connected map.</p>
          </header>
          <div className="role-ledger__rows">
            <div><span>Engineering</span><p>Frontend · Backend · Full Stack · Mobile · DevOps · QA · Data · ML · AI</p></div>
            <div><span>Product</span><p>Product Manager · Technical PM · Product Owner · Engineering Manager · Program Manager</p></div>
            <div><span>Design</span><p>UX Designer · Product Designer · UX Researcher</p></div>
            <div><span>Business</span><p>Solutions Engineer · Developer Advocate · Technical Account Manager · Sales Engineer · Business Analyst</p></div>
          </div>
        </section>

        <section className="faq-section" id="faq">
          <header className="section-heading">
            <h2>Questions about changing careers in tech</h2>
            <p>A practical starting point for software engineers and other IT professionals considering an adjacent role.</p>
          </header>
          <div className="faq-list">
            {faqItems.map((item) => (
              <details key={item.question}>
                <summary>{item.question}<span aria-hidden="true"><Circle size={16} /></span></summary>
                <p>{item.answer}</p>
              </details>
            ))}
          </div>
        </section>

        <section className="final-cta" id="early-access">
          <div>
            <p>Keep the experience. Change the direction.</p>
            <h2>Find the next role that fits.</h2>
          </div>
          <WaitlistForm source="final-cta" compact />
        </section>
      </main>

      <footer className="site-footer" id="privacy">
        <p className="site-footer__statement">Your experience is not a dead end. It is your leverage.</p>
        <div className="site-footer__meta">
          <a className="wordmark" href="#top"><Route aria-hidden="true" size={18} /> Career Path</a>
          <p>Privacy: waitlist emails are stored only to provide early-access updates.</p>
          <span>© 2026 Career Path</span>
        </div>
      </footer>
    </div>
  );
}

export default App;
